import { SMTPConfig, SMTPConfigCreate, SMTPConfigUpdate, ConnectionTestResponse, SendEmailDto, EmailResponse } from '../types/admin';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Helper function to create headers with authorization
const createHeaders = (token: string) => ({
	'Content-Type': 'application/json',
	Authorization: `Bearer ${token}`,
});

// SMTP Configuration Management
export const createSMTPConfig = async (token: string, config: SMTPConfigCreate): Promise<SMTPConfig> => {
	const response = await fetch(`${API_BASE_URL}/admin/smtp-config`, {
		method: 'POST',
		headers: createHeaders(token),
		body: JSON.stringify(config),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || 'Neuspešno kreiranje SMTP konfiguracije');
	}

	return response.json();
};

export const getSMTPConfigs = async (token: string): Promise<SMTPConfig[]> => {
	const response = await fetch(`${API_BASE_URL}/admin/smtp-config`, {
		method: 'GET',
		headers: createHeaders(token),
	});

	if (!response.ok) {
		throw new Error('Neuspešno dohvatanje SMTP konfiguracija');
	}

	return response.json();
};

export const getSMTPConfig = async (token: string, id: number): Promise<SMTPConfig> => {
	const response = await fetch(`${API_BASE_URL}/admin/smtp-config/${id}`, {
		method: 'GET',
		headers: createHeaders(token),
	});

	if (!response.ok) {
		throw new Error('Neuspešno dohvatanje SMTP konfiguracije');
	}

	return response.json();
};

export const getDefaultSMTPConfig = async (token: string): Promise<SMTPConfig> => {
	const response = await fetch(`${API_BASE_URL}/admin/smtp-config/default`, {
		method: 'GET',
		headers: createHeaders(token),
	});

	if (!response.ok) {
		throw new Error('Neuspešno dohvatanje zadate SMTP konfiguracije');
	}

	return response.json();
};

export const updateSMTPConfig = async (token: string, id: number, config: SMTPConfigUpdate): Promise<SMTPConfig> => {
	const response = await fetch(`${API_BASE_URL}/admin/smtp-config/${id}`, {
		method: 'PATCH',
		headers: createHeaders(token),
		body: JSON.stringify(config),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || 'Neuspešno ažuriranje SMTP konfiguracije');
	}

	return response.json();
};

export const setDefaultSMTPConfig = async (token: string, id: number): Promise<SMTPConfig> => {
	const response = await fetch(`${API_BASE_URL}/admin/smtp-config/${id}/set-default`, {
		method: 'PATCH',
		headers: createHeaders(token),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || 'Neuspešno postavljanje zadate SMTP konfiguracije');
	}

	return response.json();
};

export const deleteSMTPConfig = async (token: string, id: number): Promise<void> => {
	const response = await fetch(`${API_BASE_URL}/admin/smtp-config/${id}`, {
		method: 'DELETE',
		headers: createHeaders(token),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || 'Neuspešno brisanje SMTP konfiguracije');
	}
};

// Email Testing and Sending
export const testSMTPConnection = async (token: string, id?: number): Promise<ConnectionTestResponse> => {
	const url = id ? `${API_BASE_URL}/admin/email/test-connection/${id}` : `${API_BASE_URL}/admin/email/test-connection`;

	const response = await fetch(url, {
		method: 'POST',
		headers: createHeaders(token),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || 'Neuspešno testiranje SMTP konekcije');
	}

	return response.json();
};

export const testSMTPConnectionWithConfig = async (token: string, config: SMTPConfigCreate): Promise<ConnectionTestResponse> => {
	const response = await fetch(`${API_BASE_URL}/admin/email/test-connection-with-config`, {
		method: 'POST',
		headers: createHeaders(token),
		body: JSON.stringify(config),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || 'Neuspešno testiranje SMTP konekcije');
	}

	return response.json();
};

export const sendEmail = async (token: string, emailData: SendEmailDto): Promise<EmailResponse> => {
	const response = await fetch(`${API_BASE_URL}/admin/email/send`, {
		method: 'POST',
		headers: createHeaders(token),
		body: JSON.stringify(emailData),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || 'Neuspešno slanje email-a');
	}

	return response.json();
};

export const sendTestEmail = async (token: string, configId: number, testEmail: string): Promise<EmailResponse> => {
	const emailData: SendEmailDto = {
		to: testEmail,
		subject: 'SMTP Test email',
		html: `
			<h2>SMTP Test konfiguracija</h2>
			<p>Ovo je test email za verifikaciju vaše SMTP konfiguracije.</p>
			<p><strong>ID konfiguracije:</strong> ${configId}</p>
			<p><strong>Poslato u:</strong> ${new Date().toLocaleString()}</p>
			<p>Ako ste primili ovaj email, vaša SMTP konfiguracija funkcioniše ispravno!</p>
		`,
		text: `
			SMTP Test konfiguracija

			Ovo je test email za verifikaciju vaše SMTP konfiguracije.
			ID konfiguracije: ${configId}
			Poslato u: ${new Date().toLocaleString()}

			Ako ste primili ovaj email, vaša SMTP konfiguracija funkcioniše ispravno!
		`,
		smtp_config_id: configId,
	};

	return sendEmail(token, emailData);
};

// Validation helpers
export const validateEmail = (email: string): boolean => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
};

export const validatePort = (port: number): boolean => {
	return port >= 1 && port <= 65535;
};

export const validateSMTPConfig = (config: SMTPConfigCreate): string[] => {
	const errors: string[] = [];

	if (!config.name.trim()) {
		errors.push('Ime konfiguracije je obavezno');
	}

	if (!config.host.trim()) {
		errors.push('SMTP host je obavezan');
	}

	if (!validatePort(config.port)) {
		errors.push('Port mora biti između 1 i 65535');
	}

	if (!config.username.trim()) {
		errors.push('Korisničko ime je obavezno');
	}

	if (!config.password.trim()) {
		errors.push('Lozinka je obavezna');
	}

	if (!config.from_email.trim()) {
		errors.push('Od email je obavezan');
	} else if (!validateEmail(config.from_email)) {
		errors.push('Od email mora biti validna email adresa');
	}

	return errors;
};
